import React from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined'
import { Link as RouterLink } from 'react-router-dom'

import { useFeedbackCounts } from '../../hooks/useFeedbackCounts'

// Subtle lavender palette - distinct from the teal brand accent used
// for navigation + drawer, so the notification reads as a separate
// "info" signal. Kept very muted so it sits as a passive nudge.
const BAR_BG = '#efe7fa'
const BAR_BORDER = '#d4c4ec'
const BAR_ACCENT = '#673ab7'

/**
 * Subtle inline banner shown above the page header on admin pages
 * when the Evo team has un-triaged feedback waiting. Visible only
 * to EVO_ADMIN / EVO_TEAM (counts endpoint 403s for everyone else,
 * and `useFeedbackCounts` short-circuits the fetch).
 *
 * Auto-hides when the count drops to zero. No dismiss button - this
 * is a passive nudge, not an alert.
 */
const FeedbackNotificationBar: React.FC = () => {
  const { counts } = useFeedbackCounts()
  const total = counts.new_total
  const today = counts.new_today

  if (!total) return null

  const label =
    total === 1
      ? '1 new feedback item is waiting'
      : `${total} new feedback items are waiting`
  const sublabel = today > 0 ? `${today} today` : null

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        mb: 2,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: BAR_BORDER,
        bgcolor: BAR_BG,
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
      }}
    >
      <FeedbackOutlinedIcon sx={{ color: BAR_ACCENT, fontSize: 18 }} />
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" color="text.secondary">
            · {sublabel}
          </Typography>
        )}
      </Stack>
      <Button
        component={RouterLink}
        to="/admin/feedback"
        size="small"
        sx={{
          color: BAR_ACCENT,
          textTransform: 'none',
          fontWeight: 600,
          minWidth: 0,
          px: 1,
          '&:hover': { bgcolor: 'rgba(126, 87, 194, 0.08)' },
        }}
      >
        View
      </Button>
    </Box>
  )
}

export default FeedbackNotificationBar
